import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiJson } from '../apiClient';
import { useAuth } from '../AuthContext';
import { normalizeRole } from '../utils/userLocale';
import './CustomerDebtsPage.css';

const PAY_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'lipa_number', label: 'Lipa / mobile money' },
  { value: 'bank', label: 'Bank transfer' },
];

function formatTzs(n) {
  return `TZS ${Math.round(Number(n) || 0).toLocaleString()}`;
}

function DebtModal({ open, onClose, title, lead, children }) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="customer-debts-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="customer-debts-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="debt-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="debt-modal-title">{title}</h2>
        {lead ? <p className="customer-debts-modal-lead">{lead}</p> : null}
        {children}
      </div>
    </div>,
    document.body
  );
}

function CustomerDebtsPage({ onNavigate }) {
  const { user } = useAuth();
  const isOwner = normalizeRole(user?.role) === 'owner';
  const [debts, setDebts] = useState([]);
  const [summary, setSummary] = useState({ openCount: 0, totalOwedTzs: 0 });
  const [filter, setFilter] = useState('open');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [payDebtId, setPayDebtId] = useState(null);

  const [addForm, setAddForm] = useState({
    customerName: '',
    customerPhone: '',
    description: '',
    totalAmount: '',
    amountPaid: '',
    dueDate: '',
    notes: '',
  });

  const [payForm, setPayForm] = useState({
    amount: '',
    paymentMethod: 'cash',
    reference: '',
    notes: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    try {
      const q = new URLSearchParams();
      if (filter === 'all') q.set('status', 'all');
      if (search.trim()) q.set('q', search.trim());
      const data = await apiJson(`/customer-debts?${q.toString()}`);
      setDebts(data.debts || []);
      setSummary(data.summary || { openCount: 0, totalOwedTzs: 0 });
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Could not load debts.' });
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => {
    void load();
  }, [load]);

  const activeDebt = payDebtId != null ? debts.find((d) => String(d.id) === String(payDebtId)) : null;

  useEffect(() => {
    if (payDebtId != null && !activeDebt) {
      setPayDebtId(null);
    }
  }, [payDebtId, activeDebt]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setMsg(null);
    try {
      await apiJson('/customer-debts', {
        method: 'POST',
        body: JSON.stringify({
          customerName: addForm.customerName.trim(),
          customerPhone: addForm.customerPhone.trim() || null,
          description: addForm.description.trim() || null,
          totalAmount: Number(addForm.totalAmount),
          amountPaid: addForm.amountPaid ? Number(addForm.amountPaid) : 0,
          dueDate: addForm.dueDate || null,
          notes: addForm.notes.trim() || null,
        }),
      });
      setShowAdd(false);
      setAddForm({
        customerName: '',
        customerPhone: '',
        description: '',
        totalAmount: '',
        amountPaid: '',
        dueDate: '',
        notes: '',
      });
      setMsg({ type: 'success', text: 'Debt recorded.' });
      await load();
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Could not save.' });
    }
  };

  const handlePay = async (e) => {
    e.preventDefault();
    if (!payDebtId) return;
    setMsg(null);
    try {
      await apiJson(`/customer-debts/${payDebtId}/payments`, {
        method: 'POST',
        body: JSON.stringify({
          amount: Number(payForm.amount),
          paymentMethod: payForm.paymentMethod,
          reference: payForm.reference.trim() || null,
          notes: payForm.notes.trim() || null,
        }),
      });
      setPayDebtId(null);
      setPayForm({ amount: '', paymentMethod: 'cash', reference: '', notes: '' });
      setMsg({ type: 'success', text: 'Payment recorded.' });
      await load();
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Payment failed.' });
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this debt? Use only for mistakes — balance will be cleared.')) return;
    try {
      await apiJson(`/customer-debts/${id}/cancel`, { method: 'POST', body: '{}' });
      setMsg({ type: 'success', text: 'Debt cancelled.' });
      await load();
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Could not cancel.' });
    }
  };

  return (
    <div className="page-bleed customer-debts-page">
      <header className="customer-debts-header">
        <div>
          <h1>Customer debts</h1>
          <p>Track who bought on credit or paid only part of the bill — record follow-up payments here.</p>
        </div>
        <button type="button" className="customer-debts-btn customer-debts-btn--primary" onClick={() => setShowAdd(true)}>
          + Record debt
        </button>
      </header>

      <div className="customer-debts-stats">
        <div className="customer-debts-stat">
          <span>Open accounts</span>
          <strong>{summary.openCount}</strong>
        </div>
        <div className="customer-debts-stat customer-debts-stat--warn">
          <span>Total owed</span>
          <strong>{formatTzs(summary.totalOwedTzs)}</strong>
        </div>
      </div>

      <div className="customer-debts-toolbar">
        <input
          type="search"
          placeholder="Search name or phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
        />
        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="open">Open & partial</option>
          <option value="all">All (incl. paid)</option>
        </select>
        <button type="button" className="customer-debts-btn" onClick={() => load()}>
          Refresh
        </button>
      </div>

      {msg && <div className={`customer-debts-msg customer-debts-msg--${msg.type}`}>{msg.text}</div>}

      {loading ? (
        <p className="customer-debts-loading">Loading…</p>
      ) : debts.length === 0 ? (
        <p className="customer-debts-empty">No debts match. Sales with unpaid balance from POS appear here automatically.</p>
      ) : (
        <ul className="customer-debts-list">
          {debts.map((d) => (
            <li key={d.id} className={`customer-debts-card customer-debts-card--${d.status}`}>
              <div className="customer-debts-card__head">
                <div>
                  <strong>{d.customerName}</strong>
                  {d.customerPhone ? <span className="customer-debts-phone">{d.customerPhone}</span> : null}
                </div>
                <span className="customer-debts-badge">{d.status}</span>
              </div>
              <p className="customer-debts-desc">{d.description || '—'}</p>
              <div className="customer-debts-amounts">
                <span>Total {formatTzs(d.totalAmount)}</span>
                <span>Paid {formatTzs(d.paidAmount)}</span>
                <span className="customer-debts-balance">Balance {formatTzs(d.balanceAmount)}</span>
              </div>
              {d.dueDate ? <p className="customer-debts-due">Due: {d.dueDate}</p> : null}
              {d.sourceType === 'sale' && d.sourceId ? (
                <p className="customer-debts-source">From POS sale #{d.sourceId}</p>
              ) : null}
              <div className="customer-debts-card__actions">
                {d.balanceAmount > 0 && (
                  <button
                    type="button"
                    className="customer-debts-btn customer-debts-btn--primary"
                    onClick={() => {
                      setPayDebtId(d.id);
                      setPayForm({
                        amount: String(d.balanceAmount),
                        paymentMethod: 'cash',
                        reference: '',
                        notes: '',
                      });
                    }}
                  >
                    Record payment
                  </button>
                )}
                {isOwner && d.status !== 'paid' && d.status !== 'cancelled' && (
                  <button type="button" className="customer-debts-btn customer-debts-btn--ghost" onClick={() => handleCancel(d.id)}>
                    Cancel
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <DebtModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        title="Record debt (manual)"
        lead="For old balances or sales not entered in POS."
      >
            <form onSubmit={handleAdd}>
              <label>
                Customer name *
                <input required value={addForm.customerName} onChange={(e) => setAddForm((f) => ({ ...f, customerName: e.target.value }))} />
              </label>
              <label>
                Phone
                <input value={addForm.customerPhone} onChange={(e) => setAddForm((f) => ({ ...f, customerPhone: e.target.value }))} />
              </label>
              <label>
                What they owe for
                <input value={addForm.description} onChange={(e) => setAddForm((f) => ({ ...f, description: e.target.value }))} />
              </label>
              <label>
                Total amount (TZS) *
                <input type="number" min="1" required value={addForm.totalAmount} onChange={(e) => setAddForm((f) => ({ ...f, totalAmount: e.target.value }))} />
              </label>
              <label>
                Already paid (TZS)
                <input type="number" min="0" value={addForm.amountPaid} onChange={(e) => setAddForm((f) => ({ ...f, amountPaid: e.target.value }))} />
              </label>
              <label>
                Due date
                <input type="date" value={addForm.dueDate} onChange={(e) => setAddForm((f) => ({ ...f, dueDate: e.target.value }))} />
              </label>
              <div className="customer-debts-modal-actions">
                <button type="button" className="customer-debts-btn" onClick={() => setShowAdd(false)}>
                  Close
                </button>
                <button type="submit" className="customer-debts-btn customer-debts-btn--primary">
                  Save debt
                </button>
              </div>
            </form>
      </DebtModal>

      {activeDebt ? (
        <DebtModal
          open
          onClose={() => setPayDebtId(null)}
          title="Record payment"
          lead={`${activeDebt.customerName} — balance ${formatTzs(activeDebt.balanceAmount)}`}
        >
          <form onSubmit={handlePay}>
            <label>
              Amount (TZS) *
              <input
                type="number"
                min="1"
                max={activeDebt.balanceAmount}
                required
                value={payForm.amount}
                onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))}
              />
            </label>
            <label>
              Method
              <select value={payForm.paymentMethod} onChange={(e) => setPayForm((f) => ({ ...f, paymentMethod: e.target.value }))}>
                {PAY_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Reference
              <input value={payForm.reference} onChange={(e) => setPayForm((f) => ({ ...f, reference: e.target.value }))} />
            </label>
            <div className="customer-debts-modal-actions">
              <button type="button" className="customer-debts-btn" onClick={() => setPayDebtId(null)}>
                Close
              </button>
              <button type="submit" className="customer-debts-btn customer-debts-btn--primary">
                Save payment
              </button>
            </div>
          </form>
        </DebtModal>
      ) : null}
    </div>
  );
}

export default CustomerDebtsPage;
